/**
 * JSON Schema helpers used to preview function outputs: resolving $ref / nullable wrappers,
 * describing types, generating example data and Python models, and building the same schema
 * the backend sends to the model from custom object field definitions.
 */

export type JsonSchema = { [key: string]: any };

export interface ResolvedSchema {
  schema: JsonSchema;
  nullable: boolean;
  /** The last $ref followed to reach this node (or the one that closed a cycle), and its name. */
  ref?: string;
  refName?: string;
  /** The node is a $ref already being expanded higher up (a cycle). */
  recursive?: boolean;
  /** $refs on the current path; pass to child resolutions to detect cycles. */
  refPath: string[];
}

const isObject = (value: unknown): value is JsonSchema =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const refName = (ref: string): string => {
  const parts = ref.split('/');
  return decodeURIComponent(parts[parts.length - 1] || ref);
};

export function resolveRef(ref: string, root: JsonSchema): JsonSchema | undefined {
  if (!ref.startsWith('#')) return undefined;
  const pointer = ref.slice(1);
  if (!pointer) return root;
  let node: any = root;
  for (const raw of pointer.split('/').slice(1)) {
    const key = decodeURIComponent(raw).replace(/~1/g, '/').replace(/~0/g, '~');
    if (!isObject(node) && !Array.isArray(node)) return undefined;
    node = (node as any)[key];
    if (node === undefined) return undefined;
  }
  return isObject(node) ? node : undefined;
}

const isNullSchema = (node: unknown): boolean =>
  isObject(node) && (node.type === 'null' || (Array.isArray(node.enum) && node.enum.length === 1 && node.enum[0] === null));

const omit = (node: JsonSchema, ...keys: string[]): JsonSchema => {
  const out = { ...node };
  keys.forEach((k) => delete out[k]);
  return out;
};

/**
 * Unwraps $ref, single-member allOf, nullable anyOf/oneOf and `type: [x, "null"]` so callers
 * see the effective schema of a node. Keywords next to a wrapper (e.g. a description beside
 * a $ref) take precedence over the wrapped schema's own.
 */
export function resolveSchema(node: unknown, root?: JsonSchema, refPath: string[] = []): ResolvedSchema {
  const rootSchema = root || (isObject(node) ? node : {});
  if (node === true || node === undefined || node === null) return { schema: {}, nullable: false, refPath };
  if (node === false) return { schema: { not: {} }, nullable: false, refPath };
  if (!isObject(node)) return { schema: {}, nullable: false, refPath };

  let current: JsonSchema = node;
  let nullable = false;
  let lastRef: string | undefined;
  let path = refPath;

  for (let guard = 0; guard < 32; guard++) {
    if (typeof current.$ref === 'string') {
      const ref = current.$ref;
      if (path.includes(ref)) {
        return { schema: omit(current, '$ref'), nullable, ref, refName: refName(ref), recursive: true, refPath: path };
      }
      const target = resolveRef(ref, rootSchema);
      lastRef = ref;
      path = [...path, ref];
      if (!target) {
        return { schema: omit(current, '$ref'), nullable, ref, refName: refName(ref), refPath: path };
      }
      current = { ...target, ...omit(current, '$ref') };
      continue;
    }
    if (Array.isArray(current.allOf) && current.allOf.length === 1 && !current.properties && !current.type) {
      current = { ...current.allOf[0], ...omit(current, 'allOf') };
      continue;
    }
    const unionKey = Array.isArray(current.anyOf) ? 'anyOf' : Array.isArray(current.oneOf) ? 'oneOf' : null;
    if (unionKey) {
      const variants: unknown[] = current[unionKey];
      const nonNull = variants.filter((v) => !isNullSchema(v));
      if (nonNull.length < variants.length) nullable = true;
      if (nonNull.length === 1) {
        current = { ...(nonNull[0] as JsonSchema), ...omit(current, unionKey) };
        continue;
      }
      if (nonNull.length !== variants.length) {
        current = { ...current, [unionKey]: nonNull };
      }
    }
    break;
  }

  if (Array.isArray(current.type)) {
    const types = current.type.filter((t: string) => t !== 'null');
    if (types.length < current.type.length) nullable = true;
    current = { ...current, type: types.length === 1 ? types[0] : types };
  } else if (current.type === 'null') {
    nullable = true;
  }
  if (current.nullable === true) nullable = true;
  if (Array.isArray(current.enum) && current.enum.includes(null)) {
    nullable = true;
    current = { ...current, enum: current.enum.filter((v: unknown) => v !== null) };
  }

  return { schema: current, nullable, ref: lastRef, refName: lastRef ? refName(lastRef) : undefined, refPath: path };
}

export type SchemaKind = 'object' | 'array' | 'enum' | 'const' | 'union' | 'scalar' | 'any';

export interface TypeInfo {
  kind: SchemaKind;
  /** Short label such as "string", "date-time", "array<object>", "enum". */
  label: string;
  enumValues?: unknown[];
  /** Variants of a union, when kind is "union". */
  variants?: JsonSchema[];
}

const unionVariants = (schema: JsonSchema): JsonSchema[] | undefined => {
  const variants = schema.anyOf || schema.oneOf;
  if (Array.isArray(variants) && variants.length > 1) return variants;
  if (Array.isArray(schema.allOf) && schema.allOf.length > 1 && !schema.properties) return schema.allOf;
  return undefined;
};

export function describeType(node: unknown, root?: JsonSchema, refPath: string[] = [], depth = 0): TypeInfo {
  const resolved = resolveSchema(node, root, refPath);
  const { schema } = resolved;
  if (resolved.recursive) return { kind: 'object', label: resolved.refName || 'object' };
  if ('const' in schema) return { kind: 'const', label: 'const', enumValues: [schema.const] };
  if (Array.isArray(schema.enum)) return { kind: 'enum', label: 'enum', enumValues: schema.enum };

  const variants = unionVariants(schema);
  if (variants) {
    const labels = variants.map((v) => (depth > 4 ? 'any' : describeType(v, root, resolved.refPath, depth + 1).label));
    return { kind: 'union', label: Array.from(new Set(labels)).join(' | '), variants };
  }

  const type = Array.isArray(schema.type) ? schema.type : schema.type ? [schema.type] : [];
  if (type.length > 1) return { kind: 'union', label: type.join(' | ') };

  const t = type[0] || (schema.properties ? 'object' : schema.items ? 'array' : undefined);
  if (t === 'array') {
    const items = Array.isArray(schema.items) ? schema.items[0] : schema.items;
    const itemLabel = items === undefined || depth > 4 ? 'any' : describeType(items, root, resolved.refPath, depth + 1).label;
    return { kind: 'array', label: `array<${itemLabel}>` };
  }
  if (t === 'object') return { kind: 'object', label: 'object' };
  if (t === 'string' && schema.format) return { kind: 'scalar', label: String(schema.format) };
  if (t) return { kind: 'scalar', label: String(t) };
  return { kind: 'any', label: 'any' };
}

/** Human-readable constraint notes for a node, e.g. ["min 0", "max length 50"]. */
export function describeConstraints(schema: JsonSchema): string[] {
  const notes: string[] = [];
  const add = (key: string, label: string) => {
    if (schema[key] !== undefined && schema[key] !== null) notes.push(`${label} ${schema[key]}`);
  };
  add('minimum', 'min');
  add('exclusiveMinimum', '>');
  add('maximum', 'max');
  add('exclusiveMaximum', '<');
  add('multipleOf', 'multiple of');
  add('minLength', 'min length');
  add('maxLength', 'max length');
  add('minItems', 'min items');
  add('maxItems', 'max items');
  if (schema.uniqueItems) notes.push('unique items');
  if (schema.pattern) notes.push(`pattern ${schema.pattern}`);
  if (schema.default !== undefined) notes.push(`default ${JSON.stringify(schema.default)}`);
  return notes;
}

/* ------------------------------------------------------------------ */
/* Example generation                                                  */
/* ------------------------------------------------------------------ */

const FORMAT_EXAMPLES: Record<string, string> = {
  'date-time': '2025-01-15T09:30:00Z',
  date: '2025-01-15',
  time: '09:30:00',
  duration: 'P1D',
  email: 'jane.doe@example.com',
  'idn-email': 'jane.doe@example.com',
  uri: 'https://example.com',
  url: 'https://example.com',
  'uri-reference': '/path/to/resource',
  hostname: 'example.com',
  ipv4: '192.168.0.1',
  ipv6: '2001:db8::1',
  uuid: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
};

const words = (name: string): string[] =>
  name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const humanize = (name: string): string => words(name).join(' ');

const singular = (name?: string): string | undefined => {
  if (!name) return name;
  if (/ies$/i.test(name)) return name.replace(/ies$/i, 'y');
  if (/(ss|us)$/i.test(name)) return name;
  return name.replace(/s$/i, '');
};

const stringFromName = (name: string | undefined): string | undefined => {
  if (!name) return undefined;
  const w = words(name);
  const has = (...keys: string[]) => keys.some((k) => w.includes(k));
  const last = w[w.length - 1];
  if (has('email')) return 'jane.doe@example.com';
  if (has('image', 'photo', 'avatar', 'thumbnail', 'logo') && has('url', 'uri', 'link', 'src')) {
    return 'https://example.com/image.png';
  }
  if (has('url', 'uri', 'link', 'website', 'href', 'homepage')) return 'https://example.com';
  if (has('phone', 'mobile', 'tel', 'telephone')) return '+1 555-0100';
  if (last === 'id' || last === 'uuid' || last === 'guid') return '3fa85f64-5717-4562-b3fc-2c963f66afa6';
  if (last === 'at' || has('timestamp', 'datetime')) return '2025-01-15T09:30:00Z';
  if (has('date', 'dob', 'birthday') || last === 'on') return '2025-01-15';
  if (has('time')) return '09:30:00';
  if (has('first') && has('name')) return 'Jane';
  if (has('last', 'family', 'surname') && (has('name') || last === 'surname')) return 'Doe';
  if (has('company', 'organization', 'organisation', 'employer', 'brand')) return 'Acme Inc.';
  if (has('username', 'handle')) return 'janedoe';
  if (has('name')) return w.length === 1 || has('full') ? 'Jane Doe' : `Example ${humanize(name)}`;
  if (has('title', 'headline', 'subject')) return 'Example title';
  if (has('description', 'summary', 'details', 'bio', 'notes', 'note', 'comment', 'content', 'body', 'text', 'message')) {
    return 'A short description.';
  }
  if (has('address', 'street')) return '123 Main St';
  if (has('city')) return 'San Francisco';
  if (has('state', 'province', 'region')) return 'CA';
  if (has('country')) return 'United States';
  if (has('zip', 'postal', 'postcode')) return '94105';
  if (has('currency')) return 'USD';
  if (has('language', 'locale', 'lang')) return 'en';
  if (has('color', 'colour')) return '#1976d2';
  if (has('status')) return 'active';
  if (has('sku')) return 'SKU-12345';
  if (has('code')) return 'ABC123';
  if (has('category', 'type', 'kind')) return 'general';
  if (has('tag', 'tags', 'label', 'keyword')) return 'example';
  return undefined;
};

const numberFromName = (name: string | undefined, integer: boolean): number | undefined => {
  if (!name) return undefined;
  const w = words(name);
  const has = (...keys: string[]) => keys.some((k) => w.includes(k));
  if (has('lat', 'latitude')) return integer ? 37 : 37.7749;
  if (has('lng', 'lon', 'long', 'longitude')) return integer ? -122 : -122.4194;
  if (has('price', 'cost', 'amount', 'total', 'subtotal', 'fee', 'salary', 'revenue')) return integer ? 20 : 19.99;
  if (has('percent', 'percentage', 'pct')) return integer ? 50 : 42.5;
  if (has('rating', 'stars')) return integer ? 4 : 4.5;
  if (has('score', 'confidence', 'probability')) return integer ? 87 : 0.87;
  if (has('age')) return 32;
  if (has('year')) return 2025;
  if (has('month')) return 6;
  if (has('day')) return 15;
  if (has('count', 'quantity', 'qty', 'num', 'number', 'total', 'size', 'length')) return 3;
  if (has('weight', 'kg', 'grams', 'g', 'mass')) return integer ? 150 : 1.5;
  if (has('calories', 'kcal')) return integer ? 250 : 250.0;
  if (w[w.length - 1] === 'id') return 1;
  return undefined;
};

const clamp = (value: number, schema: JsonSchema, integer: boolean): number => {
  let v = value;
  const min = schema.minimum ?? (schema.exclusiveMinimum !== undefined ? schema.exclusiveMinimum + (integer ? 1 : 0.01) : undefined);
  const max = schema.maximum ?? (schema.exclusiveMaximum !== undefined ? schema.exclusiveMaximum - (integer ? 1 : 0.01) : undefined);
  if (typeof min === 'number' && v < min) v = min;
  if (typeof max === 'number' && v > max) v = max;
  return integer ? Math.round(v) : v;
};

const fitLength = (value: string, schema: JsonSchema): string => {
  let v = value;
  if (typeof schema.maxLength === 'number' && v.length > schema.maxLength) v = v.slice(0, schema.maxLength);
  if (typeof schema.minLength === 'number' && v.length < schema.minLength) v = v.padEnd(schema.minLength, 'x');
  return v;
};

const MAX_EXAMPLE_DEPTH = 12;

function exampleFor(node: unknown, root: JsonSchema, refPath: string[], name: string | undefined, depth: number): unknown {
  const resolved = resolveSchema(node, root, refPath);
  const { schema } = resolved;
  if (resolved.recursive || depth > MAX_EXAMPLE_DEPTH) return null;
  if ('const' in schema) return schema.const;
  if (Array.isArray(schema.examples) && schema.examples.length > 0) return schema.examples[0];
  if (schema.example !== undefined) return schema.example;
  if (schema.default !== undefined && schema.default !== null) return schema.default;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];

  const variants = unionVariants(schema);
  if (variants) return exampleFor(variants[0], root, resolved.refPath, name, depth + 1);

  const types: string[] = Array.isArray(schema.type) ? schema.type : schema.type ? [schema.type] : [];
  const t = types[0] || (schema.properties ? 'object' : schema.items ? 'array' : undefined);

  switch (t) {
    case 'object': {
      const out: Record<string, unknown> = {};
      const props = isObject(schema.properties) ? schema.properties : {};
      Object.entries(props).forEach(([key, prop]) => {
        out[key] = exampleFor(prop, root, resolved.refPath, key, depth + 1);
      });
      if (Object.keys(props).length === 0 && isObject(schema.additionalProperties)) {
        out.key = exampleFor(schema.additionalProperties, root, resolved.refPath, undefined, depth + 1);
      }
      return out;
    }
    case 'array': {
      const items = Array.isArray(schema.items) ? schema.items[0] : schema.items;
      if (items === undefined) return [];
      return [exampleFor(items, root, resolved.refPath, singular(name), depth + 1)];
    }
    case 'string': {
      const value = (schema.format && FORMAT_EXAMPLES[schema.format]) || stringFromName(name) || (name ? `Example ${humanize(name)}` : 'example');
      return fitLength(value, schema);
    }
    case 'integer':
      return clamp(numberFromName(name, true) ?? 1, schema, true);
    case 'number':
      return clamp(numberFromName(name, false) ?? 1.5, schema, false);
    case 'boolean':
      return true;
    case 'null':
      return null;
    default:
      return null;
  }
}

/** A realistic example value for a JSON Schema (arrays get one item). */
export function exampleFromSchema(schema: unknown, root?: JsonSchema): unknown {
  const rootSchema = root || (isObject(schema) ? schema : {});
  return exampleFor(schema, rootSchema, [], undefined, 0);
}

/* ------------------------------------------------------------------ */
/* Python (Pydantic) rendering                                         */
/* ------------------------------------------------------------------ */

const pascal = (name: string): string =>
  words(name)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('') || 'Model';

const PY_KEYWORDS = new Set([
  'False', 'None', 'True', 'and', 'as', 'assert', 'async', 'await', 'break', 'class', 'continue', 'def', 'del',
  'elif', 'else', 'except', 'finally', 'for', 'from', 'global', 'if', 'import', 'in', 'is', 'lambda', 'nonlocal',
  'not', 'or', 'pass', 'raise', 'return', 'try', 'while', 'with', 'yield',
]);

/** Pydantic model source equivalent to a JSON Schema; nested objects become their own classes. */
export function schemaToPydantic(schema: JsonSchema, rootName = 'Response'): string {
  const classes: string[] = [];
  const usedNames = new Set<string>();
  const byRef = new Map<string, string>();

  const uniqueName = (base: string): string => {
    let name = pascal(base);
    let i = 2;
    while (usedNames.has(name)) name = `${pascal(base)}${i++}`;
    usedNames.add(name);
    return name;
  };

  const typeOf = (node: unknown, hint: string, refPath: string[], depth: number): string => {
    const resolved = resolveSchema(node, schema, refPath);
    const s = resolved.schema;
    if (resolved.recursive) return `"${byRef.get(resolved.ref || '') || pascal(resolved.refName || hint)}"`;
    if (depth > MAX_EXAMPLE_DEPTH) return 'Any';
    if ('const' in s) return `Literal[${JSON.stringify(s.const)}]`;
    if (Array.isArray(s.enum) && s.enum.length > 0) {
      return `Literal[${s.enum.map((v: unknown) => JSON.stringify(v)).join(', ')}]`;
    }
    const variants = unionVariants(s);
    if (variants) {
      return `Union[${variants.map((v, i) => typeOf(v, `${hint}_option_${i + 1}`, resolved.refPath, depth + 1)).join(', ')}]`;
    }
    const types: string[] = Array.isArray(s.type) ? s.type : s.type ? [s.type] : [];
    const t = types[0] || (s.properties ? 'object' : s.items ? 'array' : undefined);
    switch (t) {
      case 'object': {
        if (!isObject(s.properties) || Object.keys(s.properties).length === 0) {
          return isObject(s.additionalProperties)
            ? `Dict[str, ${typeOf(s.additionalProperties, `${hint}_value`, resolved.refPath, depth + 1)}]`
            : 'Dict[str, Any]';
        }
        if (resolved.ref && byRef.has(resolved.ref)) return byRef.get(resolved.ref)!;
        const className = uniqueName(s.title || resolved.refName || hint);
        if (resolved.ref) byRef.set(resolved.ref, className);
        emitClass(className, s, resolved.refPath, depth);
        return className;
      }
      case 'array': {
        const items = Array.isArray(s.items) ? s.items[0] : s.items;
        return `List[${items === undefined ? 'Any' : typeOf(items, singular(hint) || `${hint}_item`, resolved.refPath, depth + 1)}]`;
      }
      case 'string':
        if (s.format === 'date-time') return 'datetime';
        if (s.format === 'date') return 'date';
        if (s.format === 'uuid') return 'UUID';
        return 'str';
      case 'integer':
        return 'int';
      case 'number':
        return 'float';
      case 'boolean':
        return 'bool';
      case 'null':
        return 'None';
      default:
        return 'Any';
    }
  };

  const emitClass = (className: string, s: JsonSchema, refPath: string[], depth: number) => {
    const required = new Set<string>(Array.isArray(s.required) ? s.required : []);
    const lines: string[] = [`class ${className}(BaseModel):`];
    if (s.description) lines.push(`    ${JSON.stringify(String(s.description))}`);
    const props = Object.entries(s.properties as JsonSchema);
    props.forEach(([key, prop]) => {
      const resolved = resolveSchema(prop, schema, refPath);
      let pyType = typeOf(prop, key, refPath, depth + 1);
      const isRequired = required.has(key);
      if (!isRequired || resolved.nullable) pyType = `Optional[${pyType}]`;
      let attr = key.replace(/[^A-Za-z0-9_]/g, '_');
      if (/^[0-9]/.test(attr)) attr = `field_${attr}`;
      if (PY_KEYWORDS.has(attr)) attr = `${attr}_`;
      const args = [isRequired ? '...' : 'None'];
      if (attr !== key) args.push(`alias=${JSON.stringify(key)}`);
      if (resolved.schema.description) args.push(`description=${JSON.stringify(String(resolved.schema.description))}`);
      lines.push(`    ${attr}: ${pyType} = Field(${args.join(', ')})`);
    });
    if (props.length === 0) lines.push('    pass');
    classes.push(lines.join('\n'));
  };

  const rootType = typeOf(schema, rootName, [], 0);
  const header = [
    'from datetime import date, datetime',
    'from typing import Any, Dict, List, Literal, Optional, Union',
    'from uuid import UUID',
    '',
    'from pydantic import BaseModel, Field',
  ].join('\n');
  const body = classes.join('\n\n\n');
  const isClass = classes.length > 0 && rootType === Array.from(usedNames)[0];
  const aliasName = usedNames.has(pascal(rootName)) ? uniqueName(`${rootName}_list`) : pascal(rootName);
  const alias = isClass ? '' : `\n\n\n${aliasName} = ${rootType}`;
  return `${header}\n\n\n${body}${alias}\n`.replace(/\n{4,}/g, '\n\n\n');
}

/* ------------------------------------------------------------------ */
/* Custom object fields -> JSON Schema                                  */
/* ------------------------------------------------------------------ */

export interface SchemaSourceField {
  name: string;
  display_name?: string;
  field_type: string;
  is_required?: boolean;
  description?: string;
  help_text?: string;
  min_length?: number | null;
  max_length?: number | null;
  min_value?: number | null;
  max_value?: number | null;
  regex_pattern?: string | null;
  allowed_values?: string[] | null;
  reference_object_id?: string | null;
  array_item_type?: string | null;
  nested_fields?: SchemaSourceField[] | null;
}

export interface SchemaSourceObject {
  id?: string;
  name?: string;
  display_name?: string;
  description?: string;
  fields?: SchemaSourceField[];
}

export type ObjectLookup = Record<string, SchemaSourceObject | undefined> | SchemaSourceObject[];

const lookupObject = (objects: ObjectLookup, id: string): SchemaSourceObject | undefined =>
  Array.isArray(objects) ? objects.find((o) => o && o.id === id) : objects[id];

const JSON_TYPES: Record<string, string> = {
  string: 'string',
  text: 'string',
  integer: 'integer',
  float: 'number',
  boolean: 'boolean',
  date: 'string',
  datetime: 'string',
  json: 'object',
  uuid: 'string',
  array: 'array',
  reference: 'string',
};

const FORMATS: Record<string, string> = { date: 'date', datetime: 'date-time', uuid: 'uuid' };

const normalizeType = (t: unknown): string => {
  const v = String(t || '').toLowerCase();
  return v in JSON_TYPES ? v : 'string';
};

const fieldDescription = (f: SchemaSourceField): string =>
  f.help_text || f.description || `${f.display_name || f.name} field`;

/*
 * The builders below must stay in step with PydanticConverter.generate_langchain_schema
 * (api/common/json_to_pydantic.py), which produces the schema the model actually receives:
 * nested_fields describe JSON objects and the items of lists of JSON (at any depth), and
 * references are expanded only at the top level.
 */

interface BuildContext {
  objects: ObjectLookup;
  visiting: Set<string>;
  referenceSchemas: Record<string, JsonSchema | undefined>;
}

function referenceSchema(refId: string, ctx: BuildContext, fallbackDescription: string): JsonSchema {
  if (ctx.visiting.has(refId)) {
    return objectSchemaFor(lookupObject(ctx.objects, refId) || { id: refId }, ctx);
  }
  const known = ctx.referenceSchemas[refId];
  if (known) return known;
  const ref = lookupObject(ctx.objects, refId);
  if (ref && Array.isArray(ref.fields) && ref.fields.length > 0) {
    return objectSchemaFor(ref, ctx);
  }
  const label = ref?.display_name || ref?.name;
  return {
    type: 'object',
    ...(label ? { title: label } : {}),
    description: label ? `${label} (fields not loaded)` : fallbackDescription,
  };
}

const hasChildren = (f: SchemaSourceField): f is SchemaSourceField & { nested_fields: SchemaSourceField[] } =>
  Array.isArray(f.nested_fields) && f.nested_fields.length > 0;

function nestedObjectSchema(fields: SchemaSourceField[], description: string): JsonSchema {
  const properties: JsonSchema = {};
  const required: string[] = [];
  fields.forEach((nf) => {
    if (!nf) return;
    properties[nf.name] = nestedFieldSchema(nf);
    if (nf.is_required) required.push(nf.name);
  });
  return { type: 'object', properties, required, description };
}

function nestedFieldSchema(f: SchemaSourceField): JsonSchema {
  const t = normalizeType(f.field_type);
  const description = fieldDescription(f);
  if (t === 'json' && hasChildren(f)) return nestedObjectSchema(f.nested_fields, description);

  const out: JsonSchema = { description, type: t === 'reference' ? 'string' : JSON_TYPES[t] };
  if (FORMATS[t]) out.format = FORMATS[t];
  if (t === 'array') {
    const itemType = f.array_item_type ? normalizeType(f.array_item_type) : 'string';
    if (itemType === 'json' && hasChildren(f)) {
      out.items = nestedObjectSchema(f.nested_fields, `One ${f.display_name || ''} item`);
    } else {
      out.items = { type: itemType === 'reference' ? 'string' : JSON_TYPES[itemType] };
      if (FORMATS[itemType]) out.items.format = FORMATS[itemType];
    }
  }
  if (Array.isArray(f.allowed_values) && f.allowed_values.length > 0) out.enum = f.allowed_values;
  return out;
}

function fieldSchema(f: SchemaSourceField, ctx: BuildContext): JsonSchema {
  const t = normalizeType(f.field_type);
  const itemType = f.array_item_type ? normalizeType(f.array_item_type) : undefined;
  const refId = f.reference_object_id || undefined;
  let out: JsonSchema = { description: fieldDescription(f) };

  if (t === 'reference' && refId) {
    out = referenceSchema(refId, ctx, out.description);
  } else if (t === 'json' && hasChildren(f)) {
    out = nestedObjectSchema(f.nested_fields, out.description);
  } else {
    out.type = JSON_TYPES[t];
  }

  if (FORMATS[t]) out.format = FORMATS[t];
  if (t === 'string' || t === 'text') {
    if (f.min_length != null) out.minLength = f.min_length;
    if (f.max_length != null) out.maxLength = f.max_length;
    if (f.regex_pattern) out.pattern = f.regex_pattern;
  }
  if (t === 'integer' || t === 'float') {
    if (f.min_value != null) out.minimum = f.min_value;
    if (f.max_value != null) out.maximum = f.max_value;
  }
  if (Array.isArray(f.allowed_values) && f.allowed_values.length > 0) out.enum = f.allowed_values;

  if (t === 'array') {
    if (itemType === 'reference' && refId) {
      out.items = referenceSchema(refId, ctx, 'Linked object');
    } else if (itemType === 'json' && hasChildren(f)) {
      out.items = nestedObjectSchema(f.nested_fields, `One ${f.display_name || ''} item`);
    } else if (itemType) {
      out.items = { type: JSON_TYPES[itemType] };
    } else {
      out.items = { type: 'string' };
    }
  }
  return out;
}

function buildFields(fields: SchemaSourceField[], ctx: BuildContext, description?: string): JsonSchema {
  const properties: JsonSchema = {};
  const required: string[] = [];
  fields.forEach((f) => {
    if (!f || !f.name) return;
    properties[f.name] = fieldSchema(f, ctx);
    if (f.is_required) required.push(f.name);
  });
  return {
    type: 'object',
    properties,
    required,
    ...(description ? { description } : {}),
  };
}

function objectSchemaFor(obj: SchemaSourceObject, ctx: BuildContext): JsonSchema {
  const label = obj.display_name || obj.name || 'Object';
  if (obj.id && ctx.visiting.has(obj.id)) {
    return {
      type: 'object',
      properties: { id: { type: 'string', format: 'uuid', description: `Reference to ${label}` } },
      required: ['id'],
    };
  }
  if (obj.id) ctx.visiting.add(obj.id);
  const schema = buildFields(obj.fields || [], ctx, obj.description || `${label} object`);
  if (obj.id) ctx.visiting.delete(obj.id);
  return schema;
}

export interface FieldsToSchemaOptions {
  description?: string;
  /** Object ids already being expanded (e.g. the object these fields belong to). */
  visiting?: Set<string>;
  /** Known schemas for referenced objects by id, e.g. their runtime schemas. */
  referenceSchemas?: Record<string, JsonSchema | undefined>;
}

/**
 * JSON Schema for a list of custom object fields, matching what the backend generates at
 * runtime. Referenced objects come from `referenceSchemas`, or are built from `objects` when
 * their fields are loaded.
 */
export function fieldsToJsonSchema(
  fields: SchemaSourceField[],
  objects: ObjectLookup = {},
  options: FieldsToSchemaOptions = {},
): JsonSchema {
  const ctx: BuildContext = {
    objects,
    visiting: options.visiting || new Set<string>(),
    referenceSchemas: options.referenceSchemas || {},
  };
  return buildFields(fields, ctx, options.description);
}

/** JSON Schema for a custom object, matching what the backend generates at runtime. */
export function objectToJsonSchema(obj: SchemaSourceObject, objects: ObjectLookup = {}): JsonSchema {
  return objectSchemaFor(obj, { objects, visiting: new Set<string>(), referenceSchemas: {} });
}

/** Ids of objects referenced by top-level fields (the only references the backend expands). */
export function referencedObjectIds(fields: SchemaSourceField[]): string[] {
  const ids = new Set<string>();
  fields.forEach((f) => {
    const t = normalizeType(f?.field_type);
    const item = f?.array_item_type ? normalizeType(f.array_item_type) : undefined;
    if (f?.reference_object_id && (t === 'reference' || (t === 'array' && item === 'reference'))) {
      ids.add(f.reference_object_id);
    }
  });
  return Array.from(ids);
}

/** Wraps an item schema the way list-returning functions are sent to the model. */
export function listSchema(itemSchema: JsonSchema, label?: string): JsonSchema {
  return {
    type: 'array',
    items: itemSchema,
    ...(label ? { description: `List of ${label} objects` } : {}),
  };
}
